import * as React from "react";
import styles from "./styles.module.scss";
import ModalBackground from "assets/img-sunwin/bg-form-1.png";
import LogoSunwin from "assets/img-sunwin/logo.png";
import IconUser from "assets/img-sunwin/icon-user.svg";
import IconLock from "assets/img-sunwin/icon-lock.svg";
import IconEye from "assets/img-sunwin/icon-eye.svg";
import IconEyeActive from "assets/img-sunwin/icon-eye-active.svg";
import BtnCreateAccount from "assets/img-sunwin/btn-taotaikhoan-mb.png";
import IconClose from "assets/img-sunwin/icon-close-step.png";
import { getSignupErrorMessage, NauErrorPayload } from "utils/authErrors";
import { setAuthSession } from "utils/authSession";

const DEFAULT_CURRENCY = process.env.REACT_APP_NAU_CURRENCY ?? "vnd";

type CreateAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type NauSignupSuccess = {
  token_info?: {
    user_id?: string;
  };
};

const CreateAccountModal: React.FunctionComponent<CreateAccountModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setApiError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const usernameError = submitted && username.trim() === "";
  const passwordError = submitted && password.trim() === "";
  const confirmPasswordEmpty = submitted && confirmPassword.trim() === "";
  const confirmPasswordMismatch = submitted && confirmPassword.trim() !== "" && confirmPassword.trim() !== password.trim();

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (
      !trimmedUsername ||
      !trimmedPassword ||
      !trimmedConfirmPassword ||
      trimmedConfirmPassword !== trimmedPassword
    ) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          my_id: {
            username: trimmedUsername,
            password: trimmedPassword,
            confirm_password: trimmedConfirmPassword,
          },
          currency: DEFAULT_CURRENCY,
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        let errorPayload: NauErrorPayload | null = null;
        try {
          errorPayload = responseText ? (JSON.parse(responseText) as NauErrorPayload) : null;
        } catch (parseError) {
          errorPayload = null;
        }

        setApiError(getSignupErrorMessage(response.status, errorPayload));
        return;
      }

      let payload: NauSignupSuccess | null = null;
      try {
        payload = responseText ? (JSON.parse(responseText) as NauSignupSuccess) : null;
      } catch (error) {
        payload = null;
      }

      setAuthSession({ userId: payload?.token_info?.user_id });
      onClose();
      window.location.href = "/";
    } catch (error) {
      setApiError("Khong the ket noi may chu, vui long thu lai.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    if (apiError) {
      setApiError(null);
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-account-title"
        style={{ backgroundImage: `url(${ModalBackground})` }}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Dong"
          disabled={isSubmitting}
        >
          <img src={IconClose} alt="Dong" />
        </button>

        <div className={styles.header}>
          <img src={LogoSunwin} alt="Sunwin" />
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.fieldGroup}>
            <label htmlFor="create-account-username" className={styles.label}>
              <img src={IconUser} alt="User" />
            </label>
            <input
              id="create-account-username"
              type="text"
              placeholder="Ten dang nhap"
              value={username}
              onChange={handleFieldChange(setUsername)}
              disabled={isSubmitting}
            />
          </div>
          {usernameError ? (
            <div className={styles.error}>Vui long nhap ten dang nhap</div>
          ) : null}

          <div className={styles.fieldGroup}>
            <label htmlFor="create-account-password" className={styles.label}>
              <img src={IconLock} alt="Password" />
            </label>
            <input
              id="create-account-password"
              type={showPassword ? "text" : "password"}
              placeholder="Mat khau"
              value={password}
              onChange={handleFieldChange(setPassword)}
              disabled={isSubmitting}
            />
            <button
              type="button"
              className={styles.toggleVisibility}
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "An mat khau" : "Hien mat khau"}
              disabled={isSubmitting}
            >
              <img
                src={showPassword ? IconEyeActive : IconEye}
                alt={showPassword ? "An mat khau" : "Hien mat khau"}
              />
            </button>
          </div>
          {passwordError ? (
            <div className={styles.error}>Vui long nhap mat khau</div>
          ) : null}

          <div className={styles.fieldGroup}>
            <label htmlFor="create-account-confirm-password" className={styles.label}>
              <img src={IconLock} alt="Confirm password" />
            </label>
            <input
              id="create-account-confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Nhap lai mat khau"
              value={confirmPassword}
              onChange={handleFieldChange(setConfirmPassword)}
              disabled={isSubmitting}
            />
            <button
              type="button"
              className={styles.toggleVisibility}
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={showConfirmPassword ? "An mat khau" : "Hien mat khau"}
              disabled={isSubmitting}
            >
              <img
                src={showConfirmPassword ? IconEyeActive : IconEye}
                alt={showConfirmPassword ? "An mat khau" : "Hien mat khau"}
              />
            </button>
          </div>
          {confirmPasswordEmpty ? (
            <div className={styles.error}>Vui long nhap lai mat khau</div>
          ) : null}
          {confirmPasswordMismatch ? (
            <div className={styles.error}>Mat khau nhap lai khong khop</div>
          ) : null}

          {apiError ? <div className={styles.error}>{apiError}</div> : null}

          <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
            <img src={BtnCreateAccount} alt="Tao tai khoan" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountModal;